'use client';

import { useIsMobile } from '@/components/hooks/use-mobile';
import Container from '@/components/layout/container/container';
import { Link } from '@/components/ui/link';
import RichTextViewer from '@/components/ui/rich-text-viewer/rich-text-viewer';
import { TrustpilotWidget } from '@/components/ui/trustpilot-widget';
import { cn } from '@/lib/utils';
import { buildFormContentFromLinkFields } from '@/lib/utils/form-data';
import type { MediaData } from '@/lib/getMediaData';
import type { Document } from '@contentful/rich-text-types';
import { Entry } from 'contentful';
import { useState } from 'react';
import type { TypeLinkSkeleton } from 'types/contentful/TypeLink';
import { SliderCarousel } from './components/slider-carousel/slider-carousel';
import { SliderContent } from './components/slider-content/slider-content';
import { SliderTabs } from './components/slider-tabs/slider-tabs';
import styles from './hero-slider.module.scss';

export type SlideItem = {
  id: string;
  tabLabel: string;
  tabIcon: {
    src: string | null;
    alt: string | null;
  };
  title: string;
  description?: Document;
  cta?: Entry<TypeLinkSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>;
  illustration?: {
    src: string | null;
    alt: string | null;
  };
  mediaData?: MediaData | null;
};

export type HeroSliderProps = {
  heading?: string;
  subHeading?: Document;
  backgroundImage?: string;
  primaryCta?: Entry<TypeLinkSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>;
  slides: SlideItem[];
  className?: string;
};

export const HeroSlider = ({
  heading,
  subHeading,
  backgroundImage,
  primaryCta,
  slides,
  className,
}: HeroSliderProps) => {
  const isMobile = useIsMobile();
  const [activeIndex, setActiveIndex] = useState(0);

  const primaryCtaFields = primaryCta?.fields;
  const primaryFormContent = primaryCtaFields
    ? buildFormContentFromLinkFields(primaryCtaFields)
    : {};

  // Handle hydration - avoid rendering until client-side
  if (isMobile === undefined) {
    return (
      <section
        className={cn(styles.component, className)}
        style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
      >
        <Container className={styles.container}>
          <div className={styles.header}>
            <TrustpilotWidget />
            {heading && <h1 className={styles.heading}>{heading}</h1>}
            {subHeading && (
              <div className={styles.subheading}>
                <RichTextViewer content={subHeading} />
              </div>
            )}
          </div>
          {/* Loading placeholder */}
          <div className={styles.loading} />
        </Container>
      </section>
    );
  }

  return (
    <section
      className={cn(styles.component, className)}
      style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
    >
      <Container className={styles.container}>
        {/* Header Area */}
        <div className={styles.header}>
          <TrustpilotWidget />
          {heading && <h1 className={styles.heading}>{heading}</h1>}
          {subHeading && (
            <div className={styles.subheading}>
              <RichTextViewer content={subHeading} />
            </div>
          )}
          {primaryCtaFields && (
            <div className={styles.primary_cta}>
              <Link
                label={primaryCtaFields.label}
                url={primaryCtaFields.url}
                type={primaryCtaFields.type}
                size="large"
                withArrow
                destination={primaryCtaFields.destination}
                ctaId={primaryCtaFields.key}
                {...primaryFormContent}
              />
            </div>
          )}
        </div>

        {/* Slider Section */}
        <div className={styles.slider}>
          {isMobile ? (
            <SliderCarousel
              slides={slides}
              activeIndex={activeIndex}
              onSlideChange={setActiveIndex}
            />
          ) : (
            <div className={styles.tabs_wrapper}>
              <SliderTabs slides={slides} activeIndex={activeIndex} onTabChange={setActiveIndex} />
              <SliderContent
                slide={slides[activeIndex]}
                activeIndex={activeIndex}
                totalSlides={slides.length}
              />
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

HeroSlider.displayName = 'HeroSlider';

export default HeroSlider;
